import { useEffect, useState } from 'react';

import {
  fetchAddressSuggestions,
  MIN_QUERY_LENGTH,
  type AddressSuggestion,
} from '../api/address-suggestions';

import { useDebouncedValue } from './use-debounced-value';

const SEARCH_DELAY_MS = 300;

export type SuggestionStatus = 'idle' | 'loading' | 'failed' | 'ready';

interface SuggestionState {
  term: string;
  status: SuggestionStatus;
  suggestions: AddressSuggestion[];
}

const IDLE: SuggestionState = { term: '', status: 'idle', suggestions: [] };

/** Address suggestions for what was typed. Results are never cached: the
 *  lookup service's terms only allow them to be shown, not kept. */
export function useAddressSuggestions(query: string, enabled: boolean) {
  const term = useDebouncedValue(query.trim(), SEARCH_DELAY_MS);
  const searchable = enabled && term.length >= MIN_QUERY_LENGTH;
  const [result, setResult] = useState<SuggestionState>(IDLE);

  useEffect(() => {
    if (!searchable) return;
    const controller = new AbortController();
    setResult({ term, status: 'loading', suggestions: [] });
    fetchAddressSuggestions(term, controller.signal)
      .then((suggestions) => setResult({ term, status: 'ready', suggestions }))
      .catch(() => {
        if (!controller.signal.aborted) setResult({ term, status: 'failed', suggestions: [] });
      });
    return () => controller.abort();
  }, [term, searchable]);

  return searchable && result.term === term ? result : IDLE;
}
